import * as React from 'react'
import * as AccountStore from '@store/account/account'

import useI18n from '@store/i18n/useI18n'
import useMenuElements from './useMenuElement'

import styled from '@theme/styled-components'
import AnimateHeight from 'react-animate-height'
import { motion, container, item } from '@utils/Motion'

import Account from '@components/account/Account'

import { CgMenuLeft } from 'react-icons/cg'
import { AiOutlineArrowRight, AiOutlineArrowDown } from 'react-icons/ai'

interface Props {
  navigation: Navigation
  current: string
}

const INITIAL_MENU_HEIGHT = 50
const MENU_PADDING = 15
const MENU_WIDTH = 300
const MENU_ITEM_HEIGHT = 100

const Menu = ({ navigation, current }: Props) => {
  const i18n = useI18n()

  const account = AccountStore.store.getState().account

  const [screenHeight, setScreenHeight] = React.useState<number>(window.innerHeight)
  const [menuHeight, setMenuHeight] = React.useState<number>(INITIAL_MENU_HEIGHT)

  const [visible, setVisible] = React.useState<boolean>(false)

  React.useLayoutEffect(() => {
    const updateDimensions = () => {
      setScreenHeight(window.innerHeight)
    }
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  if (!account) {
    return null
  }

  const openAccount = () => setVisible(true)

  const menuelements = React.useMemo(
    () => useMenuElements({ i18n, current, navigation, openAccount }),
    [i18n, current, navigation]
  )

  return (
    <>
      <MenuContainer id="menu-panel" duration={500} height={menuHeight}>
        <MenuTitleContainer
          onClick={() =>
            setMenuHeight(menuHeight === INITIAL_MENU_HEIGHT ? screenHeight - 4 * MENU_PADDING : INITIAL_MENU_HEIGHT)
          }>
          <MenuLibelle>
            <CgMenuLeft size={25} style={{ marginRight: MENU_PADDING }} />
            {i18n.t('components.globalmenu.title')}
          </MenuLibelle>
          {menuHeight === INITIAL_MENU_HEIGHT ? (
            <AiOutlineArrowRight size={25} color="#0053A1" />
          ) : (
            <AiOutlineArrowDown size={25} color="#0053A1" />
          )}
        </MenuTitleContainer>
        {menuHeight !== INITIAL_MENU_HEIGHT && (
          <MenuContent height={screenHeight - 8 * MENU_PADDING}>
            <motion.div className="container" variants={container} initial="hidden" animate="visible">
              {menuelements.map((m, i) => (
                <MenuItem as={motion.div} key={i} className="item" variants={item} onClick={m.onclick}>
                  {m.img && <MenuImage className="image" src={m.img} />}
                  <MenuItemLibelle
                    className="libelle"
                    focused={m.status === 'disabled'}
                    hoverable={m.childrens.length === 0}>
                    {m.title}
                  </MenuItemLibelle>

                  {m.childrens.length !== 0 && (
                    <SubMenuContainer>
                      {m.childrens.map((itm, idx) => (
                        <SubMenuLine key={idx} onClick={itm.onclick} focused={itm.status === 'disabled'}>
                          <SubMenuLibelle className="libelle">{itm.title}</SubMenuLibelle>
                        </SubMenuLine>
                      ))}
                    </SubMenuContainer>
                  )}
                </MenuItem>
              ))}
            </motion.div>
          </MenuContent>
        )}
      </MenuContainer>
      {visible && <Account closeAccount={() => setVisible(false)} />}
    </>
  )
}

export default Menu

const MenuContainer = styled(AnimateHeight)`
  position: absolute;
  bottom: ${MENU_PADDING}px;
  right: ${MENU_PADDING}px;
  top: ${MENU_PADDING}px;

  width: ${MENU_WIDTH}px;
  border-radius: ${MENU_PADDING}px;
  background-color: ${(props) => props.theme.colors.menuBackground};
  box-shadow: 0 12px 35px 0 rgba(33, 75, 115, 0.2);
`
const MenuTitleContainer = styled('div')`
  flex: 1;
  display: flex;
  cursor: pointer;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  margin: ${MENU_PADDING}px;
`
const MenuLibelle = styled('div')`
  ${(props) => props.theme.fonts.title};
  justify-content: center;
  align-items: center;
  display: flex;
`

const MenuContent = styled('div')<{ height: number }>`
  margin-top: ${MENU_PADDING}px;

  overflow: scroll;
  height: ${(props) => props.height}px;
`
const MenuItem = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-shadow: 0 12px 35px 0 rgba(33, 75, 115, 0.2);
  min-height: ${MENU_ITEM_HEIGHT}px;
  margin: ${2 * MENU_PADDING}px;
  border-radius: ${MENU_PADDING}px;

  ${(props) => props.theme.fonts.label};
  ${(props) => `color: ${props.theme.colors.dataMenuColor};`}

  &: hover {
    ${(props) => `background-color: ${props.theme.colors.dataChoiceBackground};`}
    .libelle {
      font-weight: bold;
    }
  }
`
const MenuImage = styled('img')`
  border-top-left-radius: ${MENU_PADDING}px;
  border-top-right-radius: ${MENU_PADDING}px;

  height: ${MENU_ITEM_HEIGHT}px;
  object-fit: cover;
`
const MenuItemLibelle = styled('div')<{ focused: boolean; hoverable: boolean }>`
  ${(props) => props.theme.fonts.label};
  ${(props) => `color: ${props.theme.colors.dataMenuColor};`}
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  border: 0px solid ${(props) => props.theme.colors.dataMenuColor};
  border-top-width: 1px;
  min-height: ${MENU_ITEM_HEIGHT * 0.5}px;

  ${(props) =>
    props.focused &&
    `
      text-decoration: underline;
      text-decoration-color: #fab200;
      text-decoration-thickness: 3px;
      color: #fab200;
    `}

  &: hover {
    ${(props) => props.hoverable && 'color: #fab200;'}
  }
`
const SubMenuContainer = styled('div')`
  padding: 10px 15px;
  padding-top: 0;
`
const SubMenuLine = styled('div')<{ focused: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  ${(props) =>
    props.focused &&
    `
      text-decoration: underline;
      text-decoration-color: #fab200;
      text-decoration-thickness: 1px;

      .libelle {
         color: #fab200;
      }
    `}
  .icon {
    color: ${(props) => (props.focused ? '#fab200' : '#0053a1')};
  }

  &: hover {
    .libelle {
      text-decoration: underline;
      text-decoration-color: #fab200;
      color: #fab200;
    }
    .icon {
      color: #fab200;
    }
  }
`
const SubMenuLibelle = styled('div')`
  ${(props) => props.theme.fonts.title};
  margin-left: 5px;
  font-size: 14px;
`
